import {
  afterNextRender, Component, ElementRef, HostListener, inject, Injector, OnDestroy, OnInit, ViewChild,
} from '@angular/core';
import {FormControl} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ActivatedRoute, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {PrimusRouterService} from '../../core/primus-router.service';
import {AppNotification, NotificationService} from '../../shared/notification.service';
import {
  backLink,
  fallbackView,
  ImportStep,
  ImportStepKey,
  ImportView,
  isViewOpen,
  MAPPING_TABS,
  nextLink,
  paramOfView,
  StepGates,
  StepLink,
  stepOfView,
  viewOfParam,
} from './step-bar/import-steps';
import {ImportSessionStore} from './import-session.store';

/** The query parameter that names the step view shown. Only the query
 * parameter changes, so the router keeps this component - and the import
 * in progress - alive. */
const VIEW_QUERY_PARAM = 'visning';
const VALUE_REVIEW_VIEW: ImportView = 'listeverdier';
/** The step bar's height on the page, which the mapping step's sticky
 * panes stick under. */
const STEP_BAR_HEIGHT_PROPERTY = '--import-step-bar-height';

/**
 * The Import module (route `/import-module`) — create one object per row
 * of an uploaded CSV / xlsx / accdb. Optionally matches each row to an
 * already-uploaded media object by filename and links it; a row whose
 * media is already linked is skipped (no update-existing path).
 *
 * The page shows one step view at a time, named by the `visning` query
 * parameter, under the step bar, which is the page's header. Its state is
 * `ImportSessionStore`, provided here, backed by the
 * `/cms_api/v1.0/raw_import` endpoints. The batch-defaults fields
 * (registration level, collection, …) are rendered with the native
 * prime-fields view for the chosen object type, so the page matches
 * ObjectPageV2 exactly.
 */
@Component({
    selector: 'app-admin-import-module',
    templateUrl: './admin-import-module.component.html',
    styleUrls: ['./admin-import-module.component.scss'],
    providers: [ImportSessionStore],
    standalone: false
})
export class AdminImportModuleComponent implements OnInit, OnDestroy {
  readonly store: ImportSessionStore = inject(ImportSessionStore);
  private readonly notificationService: NotificationService = inject(NotificationService);
  private readonly primusRouter: PrimusRouterService = inject(PrimusRouterService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly snackbar: MatSnackBar = inject(MatSnackBar);
  private readonly router: Router = inject(Router);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);

  parent: any;

  // --- steps ---
  readonly mappingTabs = MAPPING_TABS;
  // The value review step is shown. The review is created anew each time
  // it opens.
  get showValueReview(): boolean {
    return this.view === VALUE_REVIEW_VIEW;
  }
  private viewSubscription?: Subscription;
  private viewRequestSubscription?: Subscription;
  // The offset each step view was left at in the page's scroll area, which
  // is the page's host element: the global styles give it the scrolling,
  // so the step bar and the step footer stick inside it.
  private readonly scrollOffsets = new Map<ImportView, number>();
  // A resumed session asked for its view: its views start at the step bar,
  // as on a first visit.
  private forgetScrollOffsets = false;
  private profileAppliedSubscription?: Subscription;

  // --- the step bar's name box ---
  saveProfileNameControl = new FormControl<string>('', {nonNullable: true});
  // The step bar's name box for saving the configuration as a new profile
  // is open.
  savingAsNew = false;
  private readonly injector: Injector = inject(Injector);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private stepBarObserver?: ResizeObserver;

  /** The name box's input takes the focus as the box opens. */
  @ViewChild('saveAsNewName') set saveAsNewName(input: ElementRef<HTMLInputElement> | undefined) {
    if (input) {
      afterNextRender(() => input.nativeElement.focus(), {injector: this.injector});
    }
  }

  /** Keeps `--import-step-bar-height` on the page at the sticky step
   * bar's height, which changes as the profile wraps under the steps. */
  @ViewChild('stepBarWrapper') set stepBarWrapper(wrapper: ElementRef<HTMLElement> | undefined) {
    this.stepBarObserver?.disconnect();
    this.stepBarObserver = undefined;
    if (!wrapper || typeof ResizeObserver === 'undefined') {
      return;
    }
    this.stepBarObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      const height = entry.borderBoxSize?.[0]?.blockSize ?? (entry.target as HTMLElement).offsetHeight;
      this.host.nativeElement.style.setProperty(STEP_BAR_HEIGHT_PROPERTY, `${height}px`);
    });
    this.stepBarObserver.observe(wrapper.nativeElement, {box: 'border-box'});
  }

  ngOnInit(): void {
    this.parent = {
      adminType: 'admin',
      hasSubMenus: false,
      currentState: this.primusRouter.currentState()
    };
    this.store.init();
    this.viewRequestSubscription = this.store.viewRequested.subscribe(view => {
      this.forgetScrollOffsets = true;
      this.goToView(view);
    });
    this.profileAppliedSubscription = this.store.profileApplied.subscribe(counts => this.confirmProfileApplied(counts));
    this.viewSubscription = this.route.queryParamMap.subscribe(
      params => this.showView(params.get(VIEW_QUERY_PARAM)));
  }

  ngOnDestroy(): void {
    this.viewSubscription?.unsubscribe();
    this.viewRequestSubscription?.unsubscribe();
    this.profileAppliedSubscription?.unsubscribe();
    this.stepBarObserver?.disconnect();
  }

  /** Writes a stash the store is still waiting on, when the page goes away
   * before the pause after a value-review edit is over. */
  @HostListener('window:pagehide')
  flushReviewStash(): void {
    this.store.flushReviewStash();
  }

  // === steps ===================================================

  /** The step view shown. */
  get view(): ImportView {
    return this.store.view;
  }

  /** Opens the value review, which scans first when the scan is missing
   * or out of date (see `showView`). */
  openValueReview(): void {
    this.goToView(VALUE_REVIEW_VIEW);
  }

  /** Which steps can be opened now. */
  get stepGates(): StepGates {
    return {mapping: this.mappingStepOpen, later: this.laterStepsOpen};
  }

  /** The mapping step opens once the file, the table, the object type and
   * the batch defaults a run requires are set. */
  get mappingStepOpen(): boolean {
    const store = this.store;
    return !!store.dmsIdentifier && !!store.selectedTable && !!store.selectedObjectType
      && store.batchDefaultsComplete;
  }

  /** The value review and the run step open once the mapping step is open
   * and holds a mapping. */
  get laterStepsOpen(): boolean {
    return this.mappingStepOpen && this.store.hasMappings;
  }

  isViewOpen(view: ImportView): boolean {
    return isViewOpen(view, this.stepGates);
  }

  get onMappingStep(): boolean {
    return stepOfView(this.view).key === 'mapping';
  }

  /** The shown step's title and intro, under the step bar. */
  get stepHeading(): ImportStep['heading'] {
    return stepOfView(this.view).heading;
  }

  /** The count on a mapping tab: the mapping's lines, the lookups, or the
   * columns with values read as blank. */
  tabCount(view: ImportView): number {
    switch (view) {
      case 'kodeoppslag':
        return this.store.lookupDefinitions.length;
      case 'tomme-verdier':
        return this.store.blankValueColumns.length;
      default:
        return this.store.lineCount;
    }
  }

  /** The values the value review has left undecided; `null` before a
   * scan. */
  get undecidedValues(): number | null {
    return this.store.hasConceptScan ? this.store.valueReviewSummary.totals.undecided : null;
  }

  /** The footer's back: the step before this one, by name. */
  get footerBack(): StepLink | null {
    return backLink(this.view);
  }

  /** The footer's next: the step after this one, by name. */
  get footerNext(): StepLink | null {
    return nextLink(this.view);
  }

  /** The line beside the footer's next. Kilde's names the table and the
   * object type, so it waits for both. */
  get footerHint(): {step: ImportStepKey; key: string; params?: Record<string, string>} | null {
    const key = nextLink(this.view)?.hintKey;
    const step = stepOfView(this.view).key;
    if (!key) {
      return null;
    }
    if (step !== 'source') {
      return {step, key};
    }
    const {selectedTable, selectedObjectType} = this.store;
    return selectedTable && selectedObjectType
      ? {step, key, params: {table: selectedTable, objectType: this.translate.instant(selectedObjectType.name)}}
      : null;
  }

  /** Shows `view` as a new history entry, so the browser's back and
   * forward move between steps. A view that cannot be opened is ignored. */
  goToView(view: ImportView): void {
    if (view === this.view || !this.isViewOpen(view)) {
      return;
    }
    this.navigateToView(view);
  }

  /** Shows the view the query parameter names. A view that cannot be
   * opened - a reload, where the page's state is gone, or a history step
   * past what the page now holds - is replaced by the furthest one that
   * can. The review scans when there is no scan yet or the mapping changed
   * since the last one. A view shown again comes back at the scroll
   * offset it was left at. */
  private showView(param: string | null): void {
    const view = viewOfParam(param);
    if (!this.isViewOpen(view)) {
      this.navigateToView(fallbackView(this.stepGates), true);
      return;
    }
    if (view === this.view) {
      return;
    }
    this.keepScrollOffset(this.view);
    this.store.view = view;
    // Only a page with a table can show a step past the first, so a page
    // without one has nothing to stash and must not overwrite a stash a
    // resume is still offered from.
    if (this.store.selectedTable) {
      this.store.stashSourceToSession();
    }
    if (this.showValueReview) {
      this.store.scanConceptValuesIfOutOfDate();
    }
    this.restoreScrollOffset(view);
  }

  /** Remembers the offset `view` is left at in the page's scroll area.
   * After a resumed session asked for its view, the offsets from before it
   * are dropped instead, the one of the view it leaves too. */
  private keepScrollOffset(view: ImportView): void {
    if (this.forgetScrollOffsets) {
      this.forgetScrollOffsets = false;
      this.scrollOffsets.clear();
      return;
    }
    this.scrollOffsets.set(view, this.host.nativeElement.scrollTop);
  }

  /** Once `view` renders, scrolls it back to the offset it was left at,
   * or, for a view not left before, brings the step bar into view. */
  private restoreScrollOffset(view: ImportView): void {
    const offset = this.scrollOffsets.get(view);
    setTimeout(() => {
      if (offset === undefined) {
        document.getElementById('import-module-steps')?.scrollIntoView?.({block: 'nearest'});
      } else {
        this.host.nativeElement.scrollTop = offset;
      }
    });
  }

  /** Shows `view` as a new history entry, or with `replaceUrl` in place
   * of the current one. */
  private navigateToView(view: ImportView, replaceUrl?: true): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {[VIEW_QUERY_PARAM]: paramOfView(view)},
      queryParamsHandling: 'merge',
      replaceUrl,
    });
  }

  // === the step bar's profile actions ==========================

  /** Whether the typed save-name matches an already-saved profile,
   * case-insensitively (the backend upserts by name) - saving would
   * update that profile in place rather than create a new one. */
  get saveProfileNameCollides(): boolean {
    const name = this.saveProfileNameControl.value.trim().toLowerCase();
    return !!name && this.store.profiles.some(p => p.profile_name.toLowerCase() === name);
  }

  /** The saved profile names the save box completes to: those containing
   * what is typed, case-insensitively - every name while the box is
   * empty. */
  get saveProfileNameSuggestions(): string[] {
    const typed = this.saveProfileNameControl.value.trim().toLowerCase();
    return this.store.profiles.map(p => p.profile_name).filter(name => name.toLowerCase().includes(typed));
  }

  /** The save under the name typed in the step bar's name box, which
   * closes once the profile is saved; a refused save leaves its message in
   * the box. */
  async saveProfile(): Promise<void> {
    const name = this.saveProfileNameControl.value.trim();
    if (await this.store.saveProfileAs(name)) {
      this.saveProfileNameControl.setValue('');
      this.savingAsNew = false;
      this.confirmProfileSaved(this.store.selectedProfileName ?? name);
    }
  }

  /** The step bar's Lagre profil: over the loaded profile, or under a new
   * name when none is loaded. */
  saveProfileFromStepBar(): void {
    if (this.store.selectedProfileName) {
      void this.saveOverLoadedProfile();
    } else {
      this.startSaveAsNew();
    }
  }

  /** Opens the step bar's name box for a save as a new profile, offering
   * the name of the profile imported from a file when nothing is typed. */
  startSaveAsNew(): void {
    this.store.saveProfileStatus = null;
    if (!this.saveProfileNameControl.value && this.store.carriedProfileName) {
      this.saveProfileNameControl.setValue(this.store.carriedProfileName);
    }
    this.savingAsNew = true;
  }

  cancelSaveAsNew(): void {
    this.savingAsNew = false;
    this.saveProfileNameControl.setValue('');
  }

  /** Asks to delete the loaded profile. */
  deleteLoadedProfile(): void {
    if (this.store.selectedProfileId) {
      this.store.deleteProfile(this.store.selectedProfileId);
    }
  }

  /** The whole configuration, decisions included, over the loaded profile,
   * from the step bar's Lagre profil. A save is confirmed in a
   * snackbar, and a failure is an error notification with the backend's
   * reason when it gives one. */
  async saveOverLoadedProfile(): Promise<void> {
    const name = this.store.selectedProfileName;
    if (!name) {
      return;
    }
    if (await this.store.saveProfileAs(name)) {
      this.confirmProfileSaved(name);
    } else {
      this.notificationService.addNotification(new AppNotification(
        [this.store.saveProfileStatus ?? 'TRANS__IMPORT__PROFILE__ERROR__SAVE'], 'error'));
    }
  }

  /** What a profile apply put on the page, in a snackbar. */
  private confirmProfileApplied(counts: { mappings: number; lookups: number }): void {
    const text = `${this.translate.instant('TRANS__IMPORT__PROFILE__APPLIED')} · `
      + this.translate.instant('TRANS__IMPORT__PROFILE__APPLIED_COUNTS', counts);
    this.snackbar.open(text, undefined, {duration: 8000, horizontalPosition: 'center', verticalPosition: 'bottom'});
  }

  private confirmProfileSaved(name: string): void {
    this.snackbar.open(this.translate.instant('TRANS__IMPORT__PROFILE__SAVED', {name}), undefined, {
      duration: 8000, horizontalPosition: 'center', verticalPosition: 'bottom'});
  }
}
